import { internalFetch } from '../utils/fetchHelper';
import { IPagedCollection } from '@moko/utils';
import { IUser } from './userService';
import { IGroup } from './groupService';
import {
  parseCatalogCurrencies,
  type ICatalogCurrency,
  type ITenantCurrency,
} from './tenantCurrencies';
import { type ITenantTimezone } from './tenantTimezones';
import { type ITenantWorkType } from './tenantWorkConfig';

export type { ICatalogCurrency, ITenantCurrency } from './tenantCurrencies';
export {
  parseCatalogCurrencies,
  parseTenantCurrencies,
  toCurrencyCode,
  toTenantCurrencyPayload,
} from './tenantCurrencies';
export type { ITenantTimezone } from './tenantTimezones';
export {
  parseTenantTimezones,
  toTenantTimezonePayload,
} from './tenantTimezones';
export type { ITenantWorkConfig, ITenantWorkType } from './tenantWorkConfig';
export {
  emptyTenantWorkConfig,
  parseTenantWorkConfig,
  tenantWorkConfigSnapshot,
} from './tenantWorkConfig';

export interface ICreateTenantRequest {
  name: string;
  code: string;
  description: string;
  uuid: string;
  otherInfo?: string;
  usersIds?: number[];
  groupsIds?: number[];
  currencies?: ITenantCurrency[];
  timezones?: ITenantTimezone[];
  pricingTierId?: number | null;
  pricingDiscountPercentage?: number | null;
  certificationTypeIds?: number[];
  workTypes?: ITenantWorkType[];
}

export interface ICreateTenantResponse {
  tenantId: number;
}

export interface IUpdateTenantRequest {
  name: string;
  code: string;
  description: string;
  usersIds?: number[];
  groupsIds?: number[];
  otherInfo?: string;
  currencies?: ITenantCurrency[];
  timezones?: ITenantTimezone[];
  pricingTierId?: number | null;
  pricingDiscountPercentage?: number | null;
  certificationTypeIds?: number[];
  workTypes?: ITenantWorkType[];
}

export interface ITenant {
  id: number;
  name: string;
  code: string;
  description: string;
  uuid: string;
  users?: IUser[];
  groups?: IGroup[];
  currencies?: ITenantCurrency[];
  timezones?: ITenantTimezone[];
  pricingTierId?: number | null;
  pricingDiscountPercentage?: number | null;
  certificationTypeIds?: number[];
  workTypes?: ITenantWorkType[];
  otherInfo: any;
}

export const getCurrencies = async (
  abortController: AbortController
): Promise<ICatalogCurrency[]> => {
  const result = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/currencies?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
  return parseCatalogCurrencies(result);
};

export const getTenants = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string
): Promise<IPagedCollection<ITenant>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/tenants?pageSize=${pageSize}&pageNo=${pageNo}${
      orderBy ? `&orderBy=${orderBy}` : ''
    }${search ? `&search=${search}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getTenant = async (
  abortController: AbortController,
  tenantId: number
): Promise<ITenant> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/tenant?tenantId=${tenantId}&includeUsers=true&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const editTenant = async (
  abortController: AbortController,
  tenantId: number,
  tenant: IUpdateTenantRequest
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/tenant?tenantId=${tenantId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify(tenant)
  );
};

export const createTenant = async (
  abortController: AbortController,
  tenant: ICreateTenantRequest
): Promise<ICreateTenantResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/a/tenant`,
    'POST',
    abortController,
    JSON.stringify(tenant)
  );
};

export enum CloneTenantOption {
  OtherInfo = 1,
  UsersIds = 2,
  GroupsIds = 3,
  AssetStatus = 4,
  IssueStatus = 5,
  WorkStatus = 6,
  EntityMetadata = 7,
  OrderStatus = 8,
  ReferenceValue = 9,
  Labels = 10,
}

export const CLONE_TENANT_OPTIONS: {
  value: CloneTenantOption;
  label: string;
}[] = [
  { value: CloneTenantOption.OtherInfo, label: 'Other Info' },
  { value: CloneTenantOption.UsersIds, label: 'Users' },
  { value: CloneTenantOption.GroupsIds, label: 'Groups' },
  { value: CloneTenantOption.AssetStatus, label: 'Asset Statuses' },
  { value: CloneTenantOption.IssueStatus, label: 'Issue Statuses' },
  { value: CloneTenantOption.WorkStatus, label: 'Work Statuses' },
  { value: CloneTenantOption.EntityMetadata, label: 'Entity Metadata' },
  { value: CloneTenantOption.OrderStatus, label: 'Order Statuses' },
  { value: CloneTenantOption.ReferenceValue, label: 'Reference Values' },
  { value: CloneTenantOption.Labels, label: 'Labels' },
];

export interface ICloneTenantRequest {
  options: number[];
  entityMetadataSelection?: {
    values: string[];
  };
}

export interface ICloneTenantResponse {
  tenantId: number;
  results: Record<string, boolean>;
  isSuccess: boolean;
  message: string;
  correlationId: string;
}

export const cloneTenant = async (
  abortController: AbortController,
  tenantId: number,
  request: ICloneTenantRequest
): Promise<ICloneTenantResponse> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/cloneTenant?tenantId=${tenantId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'POST',
    abortController,
    JSON.stringify(request)
  );
};

export interface IChangeCurrentTenantResponse {
  message: string;
}

export const deleteTenant = async (
  abortController: AbortController,
  tenantId: number
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/tenant?tenantId=${tenantId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'DELETE',
    abortController
  );
};

export const changeCurrentTenant = async (
  abortController: AbortController,
  username: string,
  tenantId: number
): Promise<IChangeCurrentTenantResponse> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/a/changeTenant?username=${username}&tenantId=${tenantId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController
  );
};
